import React, { useContext, useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Offcanvas } from "react-bootstrap";
import Select from "react-select";
import { GlobalContext } from "../Context/GlobalState";
import { billpunchbasicauth } from "../utils/Credentials";
import { BASE_URL_API_TRANXT_UAT_API } from "../utils/Constant";

export default function AddExpenseDrawer({ month, year, months, onSaved }) {
  const { user } = useContext(GlobalContext);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({});
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [employeeError, setEmployeeError] = useState("");
  const [retry, setRetry] = useState(0);
  const submitting = useRef(false);
  const firmid = Number(user?.firm_id);
  const { username, password } = billpunchbasicauth;

  useEffect(() => {
    if (!show) return;
    const controller = new AbortController();
    setLoading(true);
    setEmployeeError("");
    setEmployees([]);
    (async () => {
      try {
        if (!Number.isInteger(firmid) || firmid <= 0) throw new Error("A valid firm is required.");
        const res = await fetch(`${BASE_URL_API_TRANXT_UAT_API}/api/Salary/getEmployee`, {
          method: "POST",
          headers: { Authorization: `Basic ${btoa(`${username}:${password}`)}`, "Content-Type": "application/json" },
          body: JSON.stringify({ firmId: String(firmid) }),
          signal: controller.signal,
        });
        const data = await res.json();
        if (!res.ok || String(data.status) !== "0" || !Array.isArray(data.data)) {
          throw new Error(data.msg || "Unable to load employees.");
        }
        setEmployees(data.data.map((employee) => ({
          value: Number(employee.empid),
          label: [employee.firstname, employee.lastname].filter(Boolean).join(" ") || String(employee.empid),
        })));
      } catch (err) {
        if (!controller.signal.aborted) setEmployeeError(err.message || "Unable to load employees.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [show, firmid, username, password, retry]);

  const open = () => {
    setForm({ empid: "", month, year: String(year), agent_onboarded: "0", expense_amount: "", expense_name: "", net_amount: "", status: "N" });
    setError("");
    setShow(true);
  };
  const close = () => { if (!submitting.current) setShow(false); };
  const change = (event) => setForm((previous) => ({ ...previous, [event.target.name]: event.target.value }));
  const submit = async (event) => {
    event.preventDefault();
    if (submitting.current) return;
    if (!employees.some((employee) => employee.value === Number(form.empid)) || Number(form.empid) <= 0) {
      setError("Please select an employee.");
      return;
    }
    if (!Number.isInteger(firmid) || firmid <= 0 || !form.expense_name.trim()) {
      setError("A valid firm and expense name are required.");
      return;
    }
    submitting.current = true;
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${BASE_URL_API_TRANXT_UAT_API}/api/Salary/Insertpetrolexpense`, {
        method: "POST",
        headers: { Authorization: `Basic ${btoa(`${username}:${password}`)}`, "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, empid: Number(form.empid), firmid, expense_name: form.expense_name.trim() }),
      });
      const data = await res.json();
      if (!res.ok || String(data.status) !== "0") throw new Error(data.msg || "Unable to add expense.");
      setShow(false);
    } catch (err) {
      setError(err.message || "Unable to add expense. Please try again.");
      return;
    } finally {
      submitting.current = false;
      setSaving(false);
    }
    onSaved();
  };

  return <>
    <Button size="sm" onClick={open}>Add Expense</Button>
    <Offcanvas show={show} onHide={close} placement="end" aria-labelledby="add-expense-title" style={{ width: "460px", maxWidth: "100%" }}>
      <Offcanvas.Header className="px-3 py-2" closeButton={!saving}><Offcanvas.Title id="add-expense-title">Add Expense</Offcanvas.Title></Offcanvas.Header>
      <Offcanvas.Body className="px-3 py-2">
        {error && <Alert className="p-2 mb-2" variant="danger">{error}</Alert>}
        {employeeError && <Alert className="p-2 mb-2" variant="danger">{employeeError} <Button size="sm" variant="outline-danger" onClick={() => setRetry((value) => value + 1)}>Retry</Button></Alert>}
        <Form onSubmit={submit}>
          <fieldset disabled={saving}>
            <Form.Group className="mb-2" controlId="expense-employee">
              <Form.Label className="mb-1 small">Employee</Form.Label>
              <Select styles={{
                control: (base) => ({ ...base, minHeight: 31, fontSize: "0.875rem" }),
                valueContainer: (base) => ({ ...base, padding: "0 8px" }),
                input: (base) => ({ ...base, margin: 0, padding: 0 }),
                dropdownIndicator: (base) => ({ ...base, padding: 4 }),
                clearIndicator: (base) => ({ ...base, padding: 4 }),
              }} inputId="expense-employee" options={employees} value={employees.find((employee) => employee.value === Number(form.empid)) || null} onChange={(option) => setForm((previous) => ({ ...previous, empid: option?.value || "" }))} isLoading={loading} isDisabled={saving || loading || !!employeeError} isClearable placeholder="Select employee" noOptionsMessage={() => "No employees available"} />
            </Form.Group>
            <Form.Group className="mb-2" controlId="expense-month"><Form.Label className="mb-1 small">Month</Form.Label><Form.Select size="sm" name="month" value={form.month || ""} onChange={change} required>{months.map((item) => <option key={item.value} value={item.value}>{item.name}</option>)}</Form.Select></Form.Group>
            <Form.Group className="mb-2" controlId="expense-year"><Form.Label className="mb-1 small">Year</Form.Label><Form.Control size="sm" name="year" type="number" min="1900" max="9999" step="1" value={form.year || ""} onChange={change} required /></Form.Group>
            <Form.Group className="mb-2" controlId="expense-name"><Form.Label className="mb-1 small">Expense Name</Form.Label><Form.Control size="sm" name="expense_name" value={form.expense_name || ""} onChange={change} required /></Form.Group>
            {[["agent_onboarded", "Agent Onboarded", "1"], ["expense_amount", "Expense Amount", "0.01"], ["net_amount", "Net Amount", "0.01"]].map(([name, label, step]) => <Form.Group className="mb-2" controlId={`expense-${name}`} key={name}><Form.Label className="mb-1 small">{label}</Form.Label><Form.Control size="sm" name={name} type="number" min="0" step={step} value={form[name] ?? ""} onChange={change} required /></Form.Group>)}
            <Form.Group className="mb-2" controlId="expense-status"><Form.Label className="mb-1 small">Status</Form.Label><Form.Select size="sm" name="status" value={form.status || "N"} onChange={change}><option value="N">Unprocessed</option><option value="Y">Processed</option></Form.Select></Form.Group>
            <div className="d-flex justify-content-end gap-2"><Button size="sm" variant="secondary" onClick={close}>Cancel</Button><Button size="sm" type="submit" disabled={saving || loading || !!employeeError || !form.empid}>{saving ? "Saving…" : "Save Expense"}</Button></div>
          </fieldset>
        </Form>
      </Offcanvas.Body>
    </Offcanvas>
  </>;
}
